import React, { useState, useCallback, useContext, createContext } from 'react';
import { s } from './TissStyles';
import { statusColors } from './TissConstants';
import { formatXml } from './TissHelpers';

// ==================== TOAST NOTIFICATIONS ====================

const ToastContext = createContext(null);

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}

const TOAST_COLORS = {
  success: { bg: '#e8f5e9', border: '#c8e6c9', color: '#2e7d32', icon: '\u2713' },
  error: { bg: '#ffebee', border: '#ffcdd2', color: '#c62828', icon: '\u2717' },
  info: { bg: '#e3f2fd', border: '#bbdefb', color: '#1565c0', icon: '\u2139' },
  warning: { bg: '#fff3e0', border: '#ffe0b2', color: '#e65100', icon: '!' },
};

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'info', duration = 5000) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, duration);
  }, []);

  const toast = useCallback({
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur),
    info: (msg, dur) => addToast(msg, 'info', dur),
    warning: (msg, dur) => addToast(msg, 'warning', dur),
  }, [addToast]);

  // Make toast callable directly: toast.success(), toast.error(), etc.
  const toastApi = {
    success: (msg, dur) => addToast(msg, 'success', dur),
    error: (msg, dur) => addToast(msg, 'error', dur),
    info: (msg, dur) => addToast(msg, 'info', dur),
    warning: (msg, dur) => addToast(msg, 'warning', dur),
  };

  return (
    <ToastContext.Provider value={toastApi}>
      {children}
      {/* Toast container */}
      <div style={{
        position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
        display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '400px',
        pointerEvents: 'none',
      }}>
        {toasts.map(t => {
          const c = TOAST_COLORS[t.type] || TOAST_COLORS.info;
          return (
            <div key={t.id} style={{
              padding: '12px 16px', borderRadius: '8px',
              backgroundColor: c.bg, border: `1px solid ${c.border}`, color: c.color,
              fontSize: '13px', fontWeight: '500', lineHeight: '1.4',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              display: 'flex', alignItems: 'flex-start', gap: '10px',
              pointerEvents: 'auto',
              animation: 'tissToastIn 0.3s ease-out',
            }}>
              <span style={{ fontSize: '16px', fontWeight: '700', flexShrink: 0, lineHeight: 1 }}>{c.icon}</span>
              <span>{t.message}</span>
              <button
                onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                style={{
                  marginLeft: 'auto', background: 'none', border: 'none', color: c.color,
                  cursor: 'pointer', fontSize: '16px', padding: '0 0 0 8px', lineHeight: 1, opacity: 0.6,
                }}
              >&times;</button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes tissToastIn {
          from { opacity: 0; transform: translateX(60px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

// ==================== STATUS BADGE ====================

export function StatusBadge({ status }) {
  const colors = statusColors[status] || statusColors.pendente;
  return <span style={{ ...s.badge, backgroundColor: colors.bg, color: colors.color }}>{colors.label || status}</span>;
}

// ==================== PILL FILTERS ====================

export function PillFilters({ items, active, onChange, style }) {
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', ...style }}>
      {items.map(item => {
        const isActive = active === item.id;
        const pillColor = item.color || '#1976d2';
        return (
          <button
            key={item.id}
            onClick={() => onChange(item.id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '5px 14px', fontSize: '12px', fontWeight: isActive ? '600' : '400',
              borderRadius: '16px', cursor: 'pointer', transition: 'all 0.2s',
              border: isActive ? `1px solid ${pillColor}` : '1px solid #ddd',
              backgroundColor: isActive ? pillColor + '14' : '#fff',
              color: isActive ? pillColor : '#666',
            }}
          >
            {item.label}
            {item.count != null && item.count > 0 && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: '18px', height: '18px', padding: '0 5px',
                borderRadius: '9px', fontSize: '10px', fontWeight: '700', lineHeight: 1,
                backgroundColor: isActive ? pillColor : '#e0e0e0',
                color: isActive ? '#fff' : '#666',
              }}>{item.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ==================== XML VIEWER ====================

export function XmlViewer({ xml, title }) {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const formatted = formatXml(xml);

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(xml).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const renderHighlighted = (text) => {
    const tokens = text.split(/(<[^>]+>)/g);
    return tokens.map((token, i) => {
      if (token.startsWith('<?')) {
        return <span key={i} style={{ color: '#999' }}>{token}</span>;
      }
      if (token.startsWith('</')) {
        return <span key={i} style={{ color: '#1565c0' }}>{token}</span>;
      }
      if (token.startsWith('<')) {
        const tagMatch = token.match(/^(<\/?)(\S+)([\s\S]*?)(\/?>)$/);
        if (tagMatch) {
          const [, openTag, name, attrs, close] = tagMatch;
          return (
            <span key={i}>
              <span style={{ color: '#1565c0' }}>{openTag}{name}</span>
              {attrs && <span style={{ color: '#7b1fa2' }}>{attrs}</span>}
              <span style={{ color: '#1565c0' }}>{close}</span>
            </span>
          );
        }
        return <span key={i} style={{ color: '#1565c0' }}>{token}</span>;
      }
      return <span key={i} style={{ color: '#2e7d32' }}>{token}</span>;
    });
  };

  return (
    <div style={{ marginBottom: '10px' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', backgroundColor: '#f5f5f5', borderRadius: open ? '8px 8px 0 0' : '8px',
          cursor: 'pointer', border: '1px solid #e0e0e0',
          transition: 'background-color 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '10px', color: '#999', transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0)', display: 'inline-block' }}>&#9654;</span>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#555' }}>{title}</span>
          <span style={{ fontSize: '11px', color: '#999' }}>({xml.length.toLocaleString()} chars)</span>
        </div>
        <button onClick={handleCopy} style={{
          padding: '3px 10px', fontSize: '11px', border: '1px solid #ccc', borderRadius: '4px',
          backgroundColor: copied ? '#e8f5e9' : '#fff', color: copied ? '#2e7d32' : '#555',
          cursor: 'pointer', transition: 'all 0.2s',
        }}>
          {copied ? 'Copiado!' : 'Copiar'}
        </button>
      </div>
      {open && (
        <div style={{
          backgroundColor: '#fafafa', border: '1px solid #e0e0e0', borderTop: 'none',
          borderRadius: '0 0 8px 8px', padding: '14px', fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', monospace",
          fontSize: '11.5px', lineHeight: '1.6', whiteSpace: 'pre', overflowX: 'auto', overflowY: 'auto',
          maxHeight: '400px', tabSize: 2,
        }}>
          {renderHighlighted(formatted)}
        </div>
      )}
    </div>
  );
}

// ==================== SUMMARY FIELD ====================

export function SummaryField({ label, value, mono }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: '11px', color: '#888', marginBottom: '2px' }}>{label}</div>
      <div style={{ fontSize: '13px', color: '#333', fontWeight: '500', fontFamily: mono ? 'monospace' : 'inherit', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {value || '-'}
      </div>
    </div>
  );
}

// ==================== STATUS FLOW INDICATOR ====================

export function StatusFlowIndicator({ currentStatus }) {
  const flowSteps = ['pendente', 'enviado', 'autorizado', 'realizado'];
  const terminalStatuses = { erro: '#c62828', negado: '#c62828', cancelado: '#757575' };
  const isTerminal = !!terminalStatuses[currentStatus];
  const currentIdx = flowSteps.indexOf(currentStatus);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0', padding: '8px 0' }}>
      {flowSteps.map((step, i) => {
        const isPast = currentIdx >= 0 && i < currentIdx;
        const isCurrent = step === currentStatus;
        const isActive = isPast || isCurrent;
        const colors = statusColors[step] || statusColors.pendente;
        return (
          <React.Fragment key={step}>
            {i > 0 && (
              <div style={{ width: '32px', height: '2px', backgroundColor: isPast ? '#2e7d32' : '#e0e0e0', flexShrink: 0 }} />
            )}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
              <div style={{
                width: isCurrent ? '28px' : '22px', height: isCurrent ? '28px' : '22px', borderRadius: '50%',
                backgroundColor: isActive ? colors.color : '#e0e0e0',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: isPast ? '12px' : '10px', color: '#fff', fontWeight: '700',
                border: isCurrent ? `3px solid ${colors.bg}` : 'none',
                boxShadow: isCurrent ? `0 0 0 2px ${colors.color}` : 'none',
                transition: 'all 0.2s',
              }}>
                {isPast ? '\u2713' : (i + 1)}
              </div>
              <span style={{ fontSize: '10px', color: isActive ? colors.color : '#aaa', fontWeight: isCurrent ? '700' : '400', textTransform: 'capitalize' }}>
                {step}
              </span>
            </div>
          </React.Fragment>
        );
      })}
      {isTerminal && (
        <>
          <div style={{ width: '32px', height: '2px', backgroundColor: terminalStatuses[currentStatus], flexShrink: 0, opacity: 0.4 }} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            <div style={{
              width: '28px', height: '28px', borderRadius: '50%',
              backgroundColor: terminalStatuses[currentStatus],
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '14px', color: '#fff', fontWeight: '700',
              boxShadow: `0 0 0 2px ${terminalStatuses[currentStatus]}33`,
            }}>
              {currentStatus === 'cancelado' ? '\u2715' : '!'}
            </div>
            <span style={{ fontSize: '10px', color: terminalStatuses[currentStatus], fontWeight: '700', textTransform: 'capitalize' }}>
              {currentStatus}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ==================== DOC HELPERS ====================

export function DocSection({ title, defaultOpen, children }) {
  return (
    <details open={defaultOpen} style={{ marginBottom: '8px' }}>
      <summary style={{ cursor: 'pointer', fontSize: '14px', fontWeight: '600', color: '#1976d2', padding: '12px 0 8px', userSelect: 'none' }}>{title}</summary>
      <div style={{ padding: '0 4px 8px' }}>{children}</div>
    </details>
  );
}

export function DocTable({ headers, rows }) {
  return (
    <table style={{ ...s.table, fontSize: '12px', marginBottom: '12px' }}>
      <thead>
        <tr>{headers.map((h, i) => <th key={i} style={{ ...s.th, fontSize: '10px' }}>{h}</th>)}</tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => <td key={ci} style={s.td}>{cell}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
